import type { JSX, ReactNode } from 'react';

import { Callout } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  note?: ReactNode;
  busy?: boolean;
  /** `destructive` pour une action qui supprime ou engage de l'argent. */
  variant?: 'default' | 'destructive';
}

/**
 * Confirmation avant une action engageante (Alert Dialog shadcn/ui), avec une
 * note d'avertissement optionnelle (`Callout` warning). Ne se ferme pas au clic
 * sur le voile ; Échap et « Annuler » appellent `onClose`. Le bouton de
 * validation ne ferme pas le dialogue : l'appelant le ferme quand l'action a
 * réussi, et `busy` le désactive pendant l'appel.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  confirmLabel,
  cancelLabel = 'Annuler',
  note,
  busy = false,
  variant = 'default',
}: ConfirmDialogProps): JSX.Element {
  const hasSubtitle = subtitle !== undefined && subtitle !== null && subtitle !== '';
  const hasNote = note !== undefined && note !== null;
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <AlertDialogContent {...(hasSubtitle ? {} : { 'aria-describedby': undefined })}>
        <AlertDialogHeader className={hasNote ? 'pb-0' : undefined}>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {hasSubtitle ? <AlertDialogDescription>{subtitle}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        {hasNote ? (
          <div className="px-6 py-4">
            <Callout variant="warning">{note}</Callout>
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel type="button">{cancelLabel}</AlertDialogCancel>
          <Button type="button" variant={variant} size="lg" onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
